import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Loader2, Lock } from "lucide-react";
import ProgressTracker from "../components/wizard/ProgressTracker";
import StepClient from "../components/wizard/StepClient";
import StepSystem from "../components/wizard/StepSystem";
import StepGoal from "../components/wizard/StepGoal";
import StepResults from "../components/wizard/StepResults";
import { DEFAULT_INPUT, normalizeInput } from "../lib/defaults";
import { calculate } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function WizardPage() {
  const { team, user } = useAuth();
  const [step, setStep] = useState(1);
  const [state, setState] = useState({
    ...DEFAULT_INPUT,
    technician: user?.email || "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  function set(key, val) { setState(s => ({ ...s, [key]: val })); }
  function setMany(patch) { setState(s => ({ ...s, ...patch })); }

  async function next() {
    if (step === 3) {
      setLoading(true);
      try {
        const r = await calculate(normalizeInput(state));
        setResult(r);
        setStep(4);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (e) {
        console.error(e);
        const status = e?.response?.status;
        if (status === 402) toast.error("Subscription not active. Admin must complete billing.");
        else toast.error(e?.response?.data?.detail || "Calculation failed.");
      } finally { setLoading(false); }
      return;
    }
    setStep(s => Math.min(4, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function prev() { setStep(s => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }
  function reset() { setState({ ...DEFAULT_INPUT, technician: user?.email || "" }); setResult(null); setStep(1); window.scrollTo({ top: 0, behavior: "smooth" }); }

  // Paywall — must have active sub to use the calculator
  const isActive = team?.stripe_status === "active";
  if (!isActive) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] bg-grid-dark flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-[#111111] border-2 border-[#F26522] p-8 text-center" data-testid="paywall">
          <div className="w-14 h-14 bg-[#F26522] flex items-center justify-center mx-auto mb-5">
            <Lock className="w-7 h-7 text-black" />
          </div>
          <h2 className="font-heading text-3xl font-black uppercase text-white tracking-tight mb-2">Subscription Required</h2>
          <p className="text-white/70 text-sm mb-6">
            {user?.role === "admin"
              ? "Activate your team's subscription to start using the calculator."
              : "Your team admin needs to activate the subscription before techs can use the calculator."}
          </p>
          {user?.role === "admin" ? (
            <Link to="/billing" data-testid="paywall-billing-link"
              className="block h-14 bg-[#F26522] text-black font-heading font-black tracking-wider uppercase text-base leading-[3.5rem] hover:bg-[#FF7A2A] transition-colors">
              Go to Billing
            </Link>
          ) : (
            <div className="text-xs text-white/50">Status: {team?.stripe_status || "incomplete"}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] bg-grid-dark">
      <ProgressTracker step={step} onJump={n => setStep(n)} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-[#0A0A0A] border-2 border-white/10 p-5 sm:p-8 lg:p-10">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.2 }}>
              {step === 1 && <StepClient state={state} set={set} setMany={setMany} />}
              {step === 2 && <StepSystem state={state} set={set} />}
              {step === 3 && <StepGoal state={state} set={set} />}
              {step === 4 && <StepResults state={state} result={result} onReset={reset} />}
            </motion.div>
          </AnimatePresence>
        </div>

        {step < 4 && (
          <div className="grid grid-cols-2 gap-3 mt-6 sticky bottom-4 z-30">
            <button type="button" data-testid="btn-back" onClick={prev} disabled={step === 1}
              className="h-16 px-4 border-2 border-white/30 bg-black text-white font-heading font-black tracking-wider uppercase text-base flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed hover:border-white transition-colors">
              <ChevronLeft className="w-5 h-5" /> Back
            </button>
            <button type="button" data-testid="btn-next" onClick={next} disabled={loading}
              className="h-16 px-4 bg-[#F26522] text-black font-heading font-black tracking-wider uppercase text-base flex items-center justify-center gap-2 hover:bg-[#FF7A2A] transition-colors disabled:opacity-60">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              {step === 3 ? (loading ? "Calculating…" : "Calculate") : "Continue"}
              {!loading && <ChevronRight className="w-5 h-5" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
